// skipcq: JS-W1028
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { formatRelativeCheckTime } from '../../utils/brewery/beerIngestionMonitoring';
import {
  BEER_INGESTION_PAUSED_REASON_REPEATED_FAILURES,
  countAutoPausedOpenFailures,
  failureRetryHelperCopy,
  formatBeerIngestionFailureCategoryLabel,
  isRetryDisabled,
  primaryRetryLabelForCategory,
  truncateFailureError,
} from '../../utils/brewery/beerIngestionFailures';
import { captureBeerIngestionFailuresAutoPauseBannerView } from '../../utils/beerIngestionAnalytics';

function DispositionChip({ disposition }) {
  const label = disposition === 'resolved' ? 'Resolved' : 'Open';
  const color = disposition === 'resolved' ? 'success' : 'warning';
  return <Chip size="small" color={color} label={label} />;
}

DispositionChip.propTypes = {
  disposition: PropTypes.string.isRequired,
};

function FailureDetailDrawer({
  item,
  open,
  onClose,
  onRetry,
  onResolve,
  actionLoading,
  pendingItemId,
}) {
  const handleRetryClick = useCallback(() => {
    if (item) {
      onRetry(item);
    }
  }, [item, onRetry]);

  const handleResolveClick = useCallback(() => {
    if (item?.itemId) {
      onResolve(item.itemId);
    }
  }, [item, onResolve]);

  if (!item) {
    return null;
  }
  const showAutoPauseAlert =
    item.autoPaused || item.pausedReason === BEER_INGESTION_PAUSED_REASON_REPEATED_FAILURES;
  const retryDisabled = isRetryDisabled(item) || actionLoading;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 420 }, p: 3 }} role="presentation">
        <Stack spacing={2}>
          <Typography variant="h6">Failure details</Typography>
          {showAutoPauseAlert ? (
            <Alert severity="warning">
              Monitoring was automatically paused after repeated failures. Resume after fixing
              the issue.
            </Alert>
          ) : null}
          <Typography variant="body2">
            <strong>Brewery:</strong>{' '}
            <Link component={RouterLink} to={`/brewery/${item.breweryId}`}>
              {item.breweryName || item.breweryId}
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            <strong>Source:</strong> {item.sourceUrl || '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Category:</strong> {formatBeerIngestionFailureCategoryLabel(item.category)}
          </Typography>
          <Typography variant="body2">
            <strong>Stage:</strong> {item.failureStage || '—'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            <strong>Error:</strong> {item.errorMessage || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {failureRetryHelperCopy(item.category)}
          </Typography>
          {item.runId ? (
            <Typography variant="body2">
              <strong>Run:</strong> {item.runId}
            </Typography>
          ) : null}
          {item.checkId ? (
            <Typography variant="body2">
              <strong>Check:</strong> {item.checkId}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={retryDisabled}
              onClick={handleRetryClick}
            >
              {pendingItemId === item.itemId && actionLoading
                ? 'Retrying…'
                : primaryRetryLabelForCategory(item.category)}
            </Button>
            {item.disposition === 'open' ? (
              <Button
                variant="outlined"
                disabled={actionLoading}
                onClick={handleResolveClick}
              >
                Resolve
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

FailureDetailDrawer.propTypes = {
  item: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
};

export default function BeerIngestionFailuresInbox({
  rows,
  summary,
  loading,
  error,
  actionError,
  actionLoading,
  pendingItemId,
  onRetry,
  onResolve,
  onRefresh,
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const autoPausedCount = useMemo(() => countAutoPausedOpenFailures(rows), [rows]);
  const showAutoPauseBanner = autoPausedCount > 0 || (summary?.autoPausedCount ?? 0) > 0;

  useEffect(() => {
    if (showAutoPauseBanner) {
      captureBeerIngestionFailuresAutoPauseBannerView(
        autoPausedCount || summary?.autoPausedCount || 0,
      );
    }
  }, [autoPausedCount, showAutoPauseBanner, summary?.autoPausedCount]);

  const handleCloseDetail = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleSourceLinkClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleTableRowClick = useCallback((event) => {
    const itemId = event.currentTarget.getAttribute('data-item-id');
    const row = rows.find((item) => item.itemId === itemId);
    if (row) {
      setSelectedItem(row);
    }
  }, [rows]);

  const handleRetryClick = useCallback((event) => {
    event.stopPropagation();
    const itemId = event.currentTarget.getAttribute('data-item-id');
    const row = rows.find((item) => item.itemId === itemId);
    if (row) {
      onRetry(row).catch(() => undefined);
    }
  }, [rows, onRetry]);

  const handleResolveClick = useCallback((event) => {
    event.stopPropagation();
    const itemId = event.currentTarget.getAttribute('data-item-id');
    if (itemId) {
      onResolve(itemId).catch(() => undefined);
    }
  }, [onResolve]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={(
        <Button color="inherit" size="small" onClick={onRefresh}>
          Retry
        </Button>
      )}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}
      {showAutoPauseBanner ? (
        <Alert severity="warning">
          Monitoring was automatically paused for{' '}
          {autoPausedCount || summary?.autoPausedCount || 0} source(s) after repeated failures.
          Resume after fixing the issue.
        </Alert>
      ) : null}
      {rows.length === 0 ? (
        <Typography color="text.secondary">No open failures for this state.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Brewery</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Last success</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Retry</TableCell>
                <TableCell>Disposition</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const retryLabel = primaryRetryLabelForCategory(row.category);
                const retryDisabled = isRetryDisabled(row) || actionLoading;
                return (
                  <TableRow
                    key={row.itemId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    data-item-id={row.itemId}
                    onClick={handleTableRowClick}
                  >
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/brewery/${row.breweryId}`}
                        onClick={handleSourceLinkClick}
                      >
                        {row.breweryName || row.breweryId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.sourceUrl || ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {row.sourceUrl || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{formatBeerIngestionFailureCategoryLabel(row.category)}</span>
                        {row.autoPaused ? (
                          <Chip size="small" color="error" label="Auto-paused" />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {formatRelativeCheckTime(row.lastSuccessAt) || '—'}
                    </TableCell>
                    <TableCell>{truncateFailureError(row.errorMessage)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={retryDisabled}
                        data-item-id={row.itemId}
                        onClick={handleRetryClick}
                      >
                        {pendingItemId === row.itemId && actionLoading ? '…' : retryLabel}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DispositionChip disposition={row.disposition} />
                        {row.disposition === 'open' ? (
                          <Button
                            size="small"
                            data-item-id={row.itemId}
                            onClick={handleResolveClick}
                            disabled={actionLoading}
                          >
                            Resolve
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <FailureDetailDrawer
        item={selectedItem}
        open={Boolean(selectedItem)}
        onClose={handleCloseDetail}
        onRetry={onRetry}
        onResolve={onResolve}
        actionLoading={actionLoading}
        pendingItemId={pendingItemId}
      />
    </Stack>
  );
}

BeerIngestionFailuresInbox.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  summary: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  actionError: PropTypes.string,
  actionLoading: PropTypes.bool.isRequired,
  pendingItemId: PropTypes.string,
  onRetry: PropTypes.func.isRequired,
  onResolve: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};
